import { useRef, useState } from 'react'

interface Props {
  onUpload: (file: File) => void
  uploading: boolean
}

export default function FileDropZone({ onUpload, uploading }: Props) {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) onUpload(file)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) onUpload(file)
    e.target.value = ''
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={[
        'flex-1 flex flex-col items-center justify-center gap-4 p-12 cursor-pointer transition-colors min-h-[280px]',
        dragging ? 'bg-brand/5' : 'bg-gray-50 hover:bg-gray-100',
      ].join(' ')}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
      />
      <div className={`w-16 h-16 rounded-2xl border-2 border-dashed flex items-center justify-center transition-colors ${dragging ? 'border-brand bg-brand/10' : 'border-gray-300'}`}>
        {uploading ? (
          <svg className="w-7 h-7 text-brand animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className={`w-7 h-7 ${dragging ? 'text-brand' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
          </svg>
        )}
      </div>
      <div className="text-center">
        <p className="text-gray-700 text-sm font-medium">
          {uploading ? 'Uploading file…' : 'Drop your time card here'}
        </p>
        <p className="text-gray-400 text-xs mt-1">
          {uploading ? 'Please wait' : 'or click to browse — PDF, image, CSV, or Excel'}
        </p>
      </div>
    </div>
  )
}
